import React, { useEffect, useState } from 'react';
import SearchLocationMenu from './SearchLocationMenu';
import { useSearchStateLocation, useSearchStateLocationDispatch } from './hooks/useSearchStateLocation';
import AnimateFadeIn from '../ui/AnimateFadeIn';
import SearchCitySelector from './SearchCitySelector';
import IconPencil from '../ui/icons/IconPencil';
import { Desktop, TabletAndBelow } from '../ui/Responsive';
import SearchModal from './SearchModal';

// Select an area - select submarket
// once submarket is chosen, show cities in that submarket

const SearchLocationSelector = () => {
    const { submarket, stateAsLocation, isLocationSelectorOpen } = useSearchStateLocation();
    const dispatch = useSearchStateLocationDispatch();
    const [displayCities, setDisplayCities] = useState(false);

    useEffect(() => {
        if (stateAsLocation.name) {
            setDisplayCities(true);
            dispatch({ type: 'closeLocationSelector' });
        }
    }, [stateAsLocation.name, dispatch]);

    useEffect(() => {
        if (submarket.submarket || stateAsLocation.name) {
            setDisplayCities(true);
        } else {
            setDisplayCities(false);
        }
    }, [submarket.submarket, stateAsLocation.name]);

    const handleSelectNewSubmarket = () => {
        dispatch({ type: 'toggleLocationSelector' });
    };

    const handleSelectState = (stateNew) => {
        if (stateNew.override_search) {
            window.location.href = stateNew.override_search_link;
            return;
        }
        dispatch({
            type: 'selectState',
            stateAsLocation: {
                name: stateNew.name,
                abbr: stateNew.alpha_2_code,
            },
        });
    };

    // { key: "greater-seattle-area", label: "Greater Seattle Area", stateLabel: "Washington", stateAbbr: "WA" }
    const handleSelectSubmarket = (submarketNew, stateNew) => {
        dispatch({ type: 'closeLocationSelector' });
        setDisplayCities(true);
        if (stateNew.override_search) {
            window.location.href = stateNew.override_search_link;
            return;
        }
        dispatch({
            type: 'selectSubmarket',
            submarket: {
                submarket: submarketNew.name,
                state: stateNew.name,
                stateAbbreviation: stateNew.alpha_2_code,
                slug: submarketNew.slug,
            },
        });
    };

    const renderMenu = () => {
        return (
            <div className="tw-mt-5 tw-px-3">
                <h4 className="tw-text-charcoal tw-text-lg tw-font-normal tw-pl-2 tw-mb-1">
                    Which area are you interested in?
                </h4>
                <p className="tw-text-charcoal tw-text-xxs tw-font-normal tw-pl-2">Select one on primary location</p>
                <SearchLocationMenu onSelectState={handleSelectState} onSelectSubmarket={handleSelectSubmarket} />
            </div>
        );
    };

    return (
        <div className="tw-mb-8">
            <div>
                <h1 className="tw-font-bold tw-text-2xl lg:tw-text-3xl tw-text-charcoal tw-mb-0">
                    <span className="tw-mb-2 lg:tw-mb-0 tw-block">Find Your Home in</span>
                    <button
                        type="button"
                        aria-label="Select location by state or submarket"
                        onClick={handleSelectNewSubmarket}
                        className="tw-border-none tw-font-bold tw-text-apple tw-transition-all tw-relative tw-pr-11 tw-bg-transparent tw-group hover:tw-text-kiwi"
                    >
                        <div className="tw-text-left tw-relative after:tw-z-0 after:tw-bottom-0 lg:after:tw-bottom-2 after:tw-left-0 after:tw-content[''] after:tw-absolute  after:tw-w-full after:tw-border-b after:tw-border-platinum tw-leading-[1.25] tw-pb-0 ">
                            <span className="tw-z-10 tw-relative tw-font-bold tw-text-2xl lg:tw-text-3xl tw-text-apple">
                                {submarket.submarket && `${submarket.submarket}, ${submarket.stateAbbreviation}`}
                                {stateAsLocation.name && `${stateAsLocation.name}`}
                                {!submarket.submarket && !stateAsLocation.name && 'Select An Area'}
                            </span>
                        </div>
                        <div className="tw-px-2 tw-absolute tw-right-0 tw-bottom-0 lg:-tw-bottom-1">
                            <span className="tw-rounded-full tw-bg-sheer tw-inline-block tw-p-2 group-hover:tw-bg-aloe">
                                <IconPencil />
                            </span>
                        </div>
                    </button>
                </h1>
            </div>

            {/* Mobile shows submarkets in a modal */}
            {/* On first open Transition triggers Can't perform a React state update on an unmounted */}
            <TabletAndBelow>
                <SearchModal
                    isShown={isLocationSelectorOpen}
                    onClose={() => dispatch({ type: 'closeLocationSelector' })}
                    headerText={'Select an Area'}
                >
                    {renderMenu()}
                </SearchModal>
                <AnimateFadeIn isVisible={displayCities}>
                    <SearchCitySelector />
                </AnimateFadeIn>
            </TabletAndBelow>

            {/* Desktop shows submarkets in a dropdown */}
            <Desktop>
                <AnimateFadeIn isVisible={isLocationSelectorOpen}>
                    <div className="tw-shadow tw-px-4 tw-py-6 tw-mt-8">{renderMenu()}</div>
                </AnimateFadeIn>
                <AnimateFadeIn isVisible={displayCities}>
                    <SearchCitySelector />
                </AnimateFadeIn>
            </Desktop>
        </div>
    );
};

export default SearchLocationSelector;
